import { useMemo } from "react";
import { DefaultRadioGroupInput } from "../../../../../../common/components/default_radio_group_input";
import { PercentageMaskedInput } from "../../../../../../common/components/masked_inputs/percentage_masked_input";
import { ControleEstatisticas } from "../../../../domain/models/controle_estatisticas";

import CascateList from "../../../../../../common/components/cascate_list";
import { ControleCentroCustoDetalhe } from "../../../../domain/models/controle_centro_custo_detalhe";

/**
 * StatisticsPanel
 */

type Props = {
  controleCentroCusto: ControleCentroCustoDetalhe | null;
  estatisticasControle: ControleEstatisticas | null;
  statusControle: number | null;
  metaControle: number | null;
  onChangeStatus: (status: number) => void;
  onChangeMeta: (meta: number) => void;
  onChangeBlur: () => void;
};

export const StatisticsPanel = ({
  controleCentroCusto,
  estatisticasControle,
  statusControle,
  metaControle,
  onChangeStatus,
  onChangeMeta,
  onChangeBlur,
}: Props) => {
  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: "Em Aberto",
        style: {
          backgroundColor: "#f8cb00",
          color: "white",
          padding: ".2rem .4rem",
          fontSize: "0.85rem",
          fontWeight: 700,
          textShadow: "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          borderRadius: "2px",
        },
      },
      {
        id: 1,
        label: "Finalizado",
        style: {
          backgroundColor: "#f3565d",
          color: "white",
          padding: ".2rem .4rem",
          fontSize: "0.85rem",
          fontWeight: 700,
          textShadow: "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          borderRadius: "2px",
        },
      },
    ];
  }, []);

  const totalInspecoes = useMemo(() => {
    let total = 0;
    if (estatisticasControle && estatisticasControle.totalA) total += estatisticasControle.totalA;
    if (estatisticasControle && estatisticasControle.totalP) total += estatisticasControle.totalP;
    if (estatisticasControle && estatisticasControle.totalC) total += estatisticasControle.totalC;
    if (estatisticasControle && estatisticasControle.totalPA) total += estatisticasControle.totalPA;
    if (estatisticasControle && estatisticasControle.totalCA) total += estatisticasControle.totalCA;
    return total;
  }, [
    estatisticasControle,
  ]);

  const indiceAprovacao = useMemo(() => {
    return totalInspecoes > 0 && estatisticasControle && estatisticasControle?.totalA ? estatisticasControle.totalA / totalInspecoes : 0;
  }, [
    totalInspecoes,
    estatisticasControle,
  ]);

  const fixedPercentual = useMemo(() => {
    return metaControle ? metaControle * 100 : 0;
  }, [
    metaControle,
  ]);

  const statusMetaControleClassName = useMemo(() => {
    if (estatisticasControle === null) return "red";
    if (totalInspecoes && estatisticasControle.totalA && metaControle) {
      if (totalInspecoes > 0 && (estatisticasControle.totalA / totalInspecoes) * 100 >= metaControle) {
        return "green";
      }
    }
    return "red";
  }, [
    estatisticasControle,
    metaControle,
    totalInspecoes,
  ]);

  const statusMetaControle = useMemo(() => {
    if (estatisticasControle === null) return "";
    if (totalInspecoes && estatisticasControle.totalA && metaControle) {
      if (totalInspecoes > 0 && (estatisticasControle.totalA / totalInspecoes) * 100 >= metaControle) {
        return "Acima da meta";
      }
    }
    return "Abaixo da meta";
  }, [
    estatisticasControle,
    metaControle,
    totalInspecoes,
  ]);

  return (
    <CascateList title="Informações Gerais" defaultOpen>
      <div className="w-full flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col text-black dark:text-white text-lg">
            <span className="font-bold">Centro de Custo</span>
            <span className="text-gray-600 dark:text-gray-300">{controleCentroCusto ? controleCentroCusto.centroCusto : "-"}</span>
          </div>

          <DefaultRadioGroupInput
            name="tipo"
            label="Status"
            options={statusOptions}
            value={statusControle}
            onValueChange={(e) => (e ? onChangeStatus(Number(e)) : null)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <PercentageMaskedInput
            name="meta"
            label="Meta (Aprovações)"
            disabled={controleCentroCusto !== null && controleCentroCusto.status === 1}
            value={fixedPercentual}
            onChangeValue={(e) => (e ? onChangeMeta(Number(e) / 100) : null)}
            onBlur={onChangeBlur}
          />

          <div className="flex flex-row items-center text-black dark:text-white text-lg">
            <span className="font-bold me-2">Status Meta: </span>
            <span className="font-bold" style={{ color: statusMetaControleClassName }}>
              {statusMetaControle}
            </span>
          </div>
        </div>

        <table className="table-auto mt-4">
          <thead>
            <tr>
              <th colSpan={5}></th>
              <th colSpan={5} className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">
                Total
              </th>
            </tr>
          </thead>
          <thead>
            <tr>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">Total Inspeções</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">I. Aprovação(%)</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">NA</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">A</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">P</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">C</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">PA</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">CA</th>
            </tr>
          </thead>
          <tbody>
            {
              estatisticasControle &&
              <tr>
                <td className="bg-[#DCFDF7] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">{totalInspecoes ?? 0}</div>
                </td>
                <td className="bg-[#DCFDF7] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    {((indiceAprovacao ?? 0) * 100).toFixed(2)}%
                  </div>
                </td>
                <td className="bg-[#8EA3DF] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    <span>{estatisticasControle.totalNA ?? 0}</span>
                  </div>
                </td>
                <td className="bg-[#26c281] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    {estatisticasControle.totalA ?? 0}
                  </div>
                </td>
                <td className="bg-[#ffff00] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    {estatisticasControle.totalP ?? 0}
                  </div>
                </td>
                <td className="bg-[#ef4836] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    {estatisticasControle.totalC ?? 0}
                  </div>
                </td>
                <td className="bg-[#ffb848] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    {estatisticasControle.totalPA ?? 0}
                  </div>
                </td>
                <td className="bg-[#ffb848] h-[1.8rem]">
                  <div className="size-full flex flex-row justify-center items-center font-bold">
                    {estatisticasControle.totalCA ?? 0}
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </CascateList>
  );
};
